import { Box, Button, Sheet, Stack, Typography, useTheme, CircularProgress, Alert, IconButton, Link, Avatar, Card, CardOverflow, AspectRatio, CardCover, CardContent, Chip, FormControl, Textarea, Input, Modal, ModalClose, AvatarGroup, Select, Option, DialogActions, ModalDialog } from "@mui/joy";
import { useContext, useState, useEffect } from "react";
import { LayoutContext } from "../context";
import { useParams, useNavigate } from "react-router";

import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import CommentRoundedIcon from '@mui/icons-material/CommentRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

import LogoTitle from "../components/LogoTitle";
import { APIGetUnionDetail, APIGetUnionPosts, APILikeUnion, APILikeUnionPost, APICreateUnionPostComment, APIUpdateUnion, APICreateUnionPost, APIDeleteUnionPost, APIEndUnion } from "../api";
import { formatDistanceToNow } from "date-fns";
import { ru } from "date-fns/locale";

// Компонент для отображения времени
const TimeAgo = ({ datetime }) => {
  if (!datetime) return null;
  
  const date = new Date(datetime);
  const timeAgo = formatDistanceToNow(date, {
    addSuffix: true,
    locale: ru
  });
  
  return timeAgo
    .replace('about', '')
    .replace('less than a minute', 'только что')
    .replace('minute', 'мин.')
    .replace('hour', 'час')
    .replace('day', 'день')
    .replace('days', 'дней')
    .replace('ago', 'назад')
    .trim();
};


// Новый компонент для формы создания поста
const CreatePostForm = ({ onSubmit, onCancel }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [mediaFiles, setMediaFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content.trim()) return;

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('content', content);
            
            mediaFiles.forEach(file => {
                formData.append('media_files', file);
            });

            await onSubmit(formData);
            setTitle('');
            setContent('');
            setMediaFiles([]);
        } catch (error) {
            console.error('Error creating post:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setMediaFiles(prev => [...prev, ...files]);
    };

    const removeFile = (index) => {
        setMediaFiles(prev => prev.filter((_, i) => i !== index));
    };

  return (
    <Card sx={{ mb: 3, border: '2px dashed', borderColor: 'primary.300' }}>
      <CardContent>
        <Typography level="h4" sx={{ mb: 2 }}>Создать пост</Typography>
        
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <FormControl>
              <Input
                placeholder="Заголовок (необязательно)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormControl>
            
            <FormControl>
              <Textarea
                placeholder="Текст поста..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                minRows={3}
                required
              />
            </FormControl>

            {/* Выбор файлов */}
            <FormControl>
              <Button
                component="label"
                variant="outlined"
                startDecorator={<CloudUploadRoundedIcon />}
              >
                Добавить медиа
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
              </Button>
              {mediaFiles.length > 0 && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {mediaFiles.map((file, index) => (
                    <Chip
                      key={index}
                      variant="soft"
                      onDelete={() => removeFile(index)}
                    >
                      {file.name}
                    </Chip>
                  ))}
                </Stack>
              )}
            </FormControl>

            {/* Кнопки действий */}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={!content.trim()}
              >
                Опубликовать
              </Button>
            </Stack>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};


// Компонент поста союза
const UnionPost = ({ post, onLike, onCreateComment, onDelete, token }) => {
  const [commentContent, setCommentContent] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleLike = async () => {
    if (isLiking) return;
    setIsLiking(true);
    try {
      await onLike(post.id);
    } finally {
      setIsLiking(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentContent.trim()) return;

    try {
      await onCreateComment(post.id, commentContent);
      setCommentContent('');
      setShowComments(false);
    } catch (error) {
      console.error('Error creating comment:', error);
    }
  };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            await onDelete(post.id);
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error('Error deleting post:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
    };

    return (
        <>
            <Card
                sx={{
                    // minHeight: 300,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: "xl",
                    border: '0',
                    boxShadow: "0px 7px 20px #8888884c",
                    mb: 3
                }}
            >
                {post.media_files && post.media_files.length > 0 && (
                    <CardOverflow>
                        <AspectRatio ratio="16/9">
                            <img
                                src={post.media_files[0].media_file}
                                alt={post.title}
                                loading="lazy"
                            />
                        </AspectRatio>
                    </CardOverflow>
                )}

                <CardContent sx={{ justifyContent: "space-between" }}>
                    <Stack direction={'column'} spacing={2}>
                        {/* Заголовок и информация об авторе */}
                        {post.title && (
                            <Typography level="h4">
                            {post.title}
                            </Typography>
                        )}
                    
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar 
                                src={post.author_profile?.photo_file_path} 
                                size="sm" 
                            />
                            <Box>
                            <Typography level="body-sm" fontWeight="lg">
                                {post.author_profile?.name}
                            </Typography>
                            <Typography level="body-xs" color="neutral">
                                <TimeAgo datetime={post.date_publish} />
                            </Typography>
                            </Box>
                        </Stack>

                        {/* Контент поста */}
                        <Typography level="body-sm">
                            {post.content}
                        </Typography>

                        {/* Кнопки действий */}
                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1} alignItems={'center'}>
                                <IconButton
                                    size="sm"
                                    variant={post.is_liked ? "soft" : "soft"}
                                    color="danger"
                                    onClick={handleLike}
                                    disabled={isLiking}
                                >
                                    {post.is_liked ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                                </IconButton>
                                <Typography level="body-sm">
                                    {post.likes_count || 0}
                                </Typography>
                                
                                <IconButton
                                    size="sm"
                                    variant={showComments ? "soft" : "soft"}
                                    color="neutral"
                                    onClick={() => setShowComments(!showComments)}
                                >
                                    <CommentRoundedIcon />
                                </IconButton>
                                <Typography level="body-sm">
                                    {post.comments_count || 0}
                                </Typography>
                            </Stack>

                            {post.can_delete && (
                                <IconButton
                                    variant="solid"
                                    color="danger"
                                    size="sm"
                                    onClick={handleDeleteClick}
                                    disabled={isDeleting}
                                    sx={{
                                        // position: 'absolute',
                                        // top: 8,
                                        // right: 8,
                                        // zIndex: 10
                                    }}
                                >
                                    <DeleteRoundedIcon />
                                </IconButton>
                            )}
                        </Stack>

                        {/* Комментарии */}
                        {showComments && (
                            <Stack spacing={2} sx={{ mt: 2 }}>
                                {/* Форма добавления комментария */}
                                <FormControl>
                                    <Stack direction="row" spacing={1}>
                                    <Input
                                        placeholder="Напишите комментарий..."
                                        value={commentContent}
                                        onChange={(e) => setCommentContent(e.target.value)}
                                        sx={{ flex: 1 }}
                                    />
                                    <IconButton 
                                        onClick={handleSubmitComment}
                                        disabled={!commentContent.trim()}
                                    >
                                        <SendRoundedIcon />
                                    </IconButton>
                                    </Stack>
                                </FormControl>
                                
                                {/* Список комментариев добавить здесь */}
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Диалог подтверждения удаления */}
            <Modal open={deleteDialogOpen} onClose={handleCancelDelete}>
                <ModalDialog sx={{ borderRadius: 'xl' }}>
                    <Typography level="title-md" sx={{ fontWeight: 700 }}>Удаление поста</Typography>
                    <Typography level="body-sm">
                        Вы уверены, что хотите удалить этот пост? Это действие нельзя отменить.
                    </Typography>
                    <Stack direction={'row'} spacing={1} justifyContent={'flex-start'}>
                        <Button 
                            variant="outlined" 
                            onClick={handleCancelDelete}
                            disabled={isDeleting}
                        >
                            Отмена
                        </Button>
                        <Button 
                            variant="solid" 
                            color="danger" 
                            onClick={handleConfirmDelete}
                            loading={isDeleting}
                        >
                            Удалить
                        </Button>
                    </Stack>
                </ModalDialog>
                
            </Modal>
        </>
    );
};

export default function UnionPage() {
    const theme = useTheme();
    const { token, isMobile } = useContext(LayoutContext);
    const { unionId } = useParams();
    const navigate = useNavigate();

    const [union, setUnion] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Функция для закрытия страницы
    const handleClose = () => {
        navigate('/unions'); // Возвращаем на список союзов
    };

    useEffect(() => {
        if (unionId) {
            loadUnionData();
            loadPosts();
        }
    }, [unionId, token]);

    // Загрузка данных союза
    const loadUnionData = async () => {
        try {
            //   setLoading(true);
            const response = await APIGetUnionDetail(token, unionId);
            setUnion(response);
        } catch (err) {
            setError('Ошибка загрузки данных союза');
            console.error('Error loading union:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUnion = async (unionId, unionData) => {
        try {
            await APIUpdateUnion(token, unionId, unionData);
            loadUnionData(); // Перезагружаем данные союза
        } catch (err) {
            console.error('Error updating union:', err);
            throw err;
        }
    };

    const handleCreatePost = async (unionId, postData) => {
        try {
            await APICreateUnionPost(token, unionId, postData);
            loadPosts(); // Перезагружаем посты
        } catch (err) {
            console.error('Error creating post:', err);
            throw err;
        }
    };

    // Загрузка постов
    const loadPosts = async () => {
        try {
        const response = await APIGetUnionPosts(token, unionId);
        setPosts(response.posts || []);
        } catch (err) {
        console.error('Error loading posts:', err);
        }
    };

    // Обработчики лайков и комментариев
    const handleLikeUnion = async () => {
        try {
        await APILikeUnion(token, unionId, !union.is_liked);
        loadUnionData(); // Обновляем данные
        } catch (err) {
        console.error('Error liking union:', err);
        }
    };

    const handleLikePost = async (postId) => {
        try {
        await APILikeUnionPost(token, postId);
        loadPosts(); // Обновляем посты
        } catch (err) {
        console.error('Error liking post:', err);
        }
    };

    const handleCreateComment = async (postId, content) => {
        try {
        await APICreateUnionPostComment(token, postId, content);
        loadPosts(); // Обновляем посты
        } catch (err) {
        console.error('Error creating comment:', err);
        throw err;
        }
    };

    const handleDeletePost = async (postId) => {
        try {
            await APIDeleteUnionPost(token, postId);
            loadPosts(); // Перезагружаем посты после удаления
        } catch (err) {
            console.error('Error deleting post:', err);
            throw err;
        }
    };

  // Для мобильных устройств - модальное окно
  if (isMobile) {
    return (
      <Modal open={true} onClose={handleClose}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'background.body',
            overflow: 'auto',
          }}
        >
          {/* <ModalClose sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1000 }} /> */}
          <UnionContent
            union={union}
            posts={posts}
            loading={loading}
            error={error}
            onLikeUnion={handleLikeUnion}
            onLikePost={handleLikePost}
            onCreateComment={handleCreateComment}
            onUpdateUnion={handleUpdateUnion}
            onCreatePost={handleCreatePost}
            onDeletePost={handleDeletePost}
            onClose={handleClose}
        />
        </Box>
      </Modal>
    );
  }

  // Для десктопа - обычная страница
  return (
    <UnionContent
        union={union}
        posts={posts}
        loading={loading}
        error={error}
        onLikeUnion={handleLikeUnion}
        onLikePost={handleLikePost}
        onCreateComment={handleCreateComment}
        onUpdateUnion={handleUpdateUnion}
        onCreatePost={handleCreatePost}
        onClose={handleClose}
    />
  );
}

// Выносим основной контент в отдельный компонент
function UnionContent({ union, posts, loading, error, onLikeUnion, onLikePost, onCreateComment, onClose, onUpdateUnion, onCreatePost, onDeletePost }) {
  const navigate = useNavigate();
  const { token, currentUser } = useContext(LayoutContext);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUnion, setEditedUnion] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [isEndingUnion, setIsEndingUnion] = useState(false);

  // Проверяем, является ли текущий пользователь участником союза
  const isMember = union?.current_user_member !== null;
  const isAdmin = union?.current_user_member?.role === 'admin';

  // Функция для завершения союза
  const handleEndUnion = async () => {
    if (!isAdmin) return;
    
    const confirmed = window.confirm('Вы уверены, что хотите завершить этот союз? Это действие нельзя отменить.');
    
    if (!confirmed) return;
    
    setIsEndingUnion(true);
    try {
      // Вызываем API для завершения союза
      await APIEndUnion(token, union.id);
      
      // Показываем сообщение об успехе
      alert('Союз успешно завершен');
      
      // Возвращаем на страницу списка союзов
      navigate('/unions');
        
    } catch (error) {
      console.error('Error ending union:', error);
      alert('Ошибка при завершении союза');
    } finally {
      setIsEndingUnion(false);
    }
  };

  useEffect(() => {
      if (union) {
      setEditedUnion({
          name: union.name || '',
          description: union.description || '',
          status: union.status || 'together'
      });
      }
  }, [union]);

  const handleSaveUnion = async () => {
      if (!isAdmin) return;
      
        setIsUpdating(true);
      try {
        await onUpdateUnion(union.id, editedUnion);
        setIsEditing(false);
      } catch (error) {
        console.error('Error updating union:', error);
      } finally {
        setIsUpdating(false);
      }
  };

  const handleCreatePost = async (formData) => {
          setIsCreatingPost(true);
      try {
          await onCreatePost(union.id, formData);
          setShowCreatePost(false);
      } catch (error) {
          console.error('Error creating post:', error);
          throw error;
      } finally {
          setIsCreatingPost(false);
      }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert color="danger">{error}</Alert>
      </Box>
    );
  }

  if (!union) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert color="warning">Союз не найден</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Stack
        spacing={3}
        sx={{
          px: { xs: 2, sm: 5 },
          pt: 2,
          pb: 2,
          width: "100%",
          margin: "0 auto",
          mb: 10
        }}
      >
        {/* Заголовок с кнопкой назад */}
        <Stack direction={'row'} spacing={2} sx={{ alignItems: "center" }}>
            <IconButton onClick={onClose}>
                <ArrowBackRoundedIcon />
            </IconButton>
            <Stack direction={'row'} spacing={1} sx={{ alignItems: "center", flex: 1 }}>
                <Typography level="h2" component="h1">
                    Союз
                </Typography>
            </Stack>
            
            {/* Кнопка лайка */}
            {!isMember && (
                <Button
                    startDecorator={union.is_liked ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                    variant={union.is_liked ? "solid" : "outlined"}
                    color="danger"
                    onClick={onLikeUnion}
                    sx={{ width: 'fit-content' }}
                >
                    {union.is_liked ? 'Вам нравится' : 'Нравится'}
                </Button>
            )}

            {/* Кнопка редактирования для участников */}
            {isAdmin && (
                <Button
                    variant={isEditing ? "solid" : "outlined"}
                    onClick={() => isEditing ? handleSaveUnion() : setIsEditing(true)}
                    loading={isUpdating}
                >
                    {isEditing ? 'Сохранить' : 'Редактировать'}
                </Button>
            )}
        </Stack>

        {/* Информация о союзе */}
        <Stack direction={"column"} sx={{ width: "100%" }} spacing={3}>
            <Stack direction={"row"} spacing={2} justifyContent={"flex-start"} alignItems={'center'}>
                <AvatarGroup size="lg">
                    {union.members?.slice(0, 2).map((member, index) => (
                        <Avatar
                        key={member.id}
                        src={member.profile?.photo_file_path}
                        alt={member.profile?.name}
                        sx={{
                            zIndex: union.members.length - index,
                            marginLeft: index > 0 ? '-10px' : 0
                        }}
                        />
                    ))}
                </AvatarGroup>
                {/* Название и описание */}
                <Stack spacing={1}>
                    {isEditing ? (
                        <Input
                            value={editedUnion.name}
                            onChange={(e) => setEditedUnion(prev => ({...prev, name: e.target.value}))}
                            placeholder="Название союза"
                        />
                    ) : (
                        <Typography level="h4" color="primary" sx={{ width: '100%', lineHeight: 1.1 }}>
                            {union.name || `Союз ${union.members?.[0]?.profile?.name} и ${union.members?.[1]?.profile?.name}`}
                        </Typography>
                    )}
                </Stack>
            </Stack>
          <Sheet color="background.body" variant="soft" sx={{ width: "100%", borderRadius: "xl", boxShadow: "0px 7px 20px #8888884c" }}>
            <Stack direction={"row"} justifyContent={"space-between"} sx={{ py: 2, px: 3 }}>
              {/* Статистика */}
              <Stack justifyContent={"center"}>
                <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                  Постов
                </Typography>
                <Typography level="body-sm" sx={{ textAlign: 'center', fontWeight: 600 }}>
                  {union.posts_count || 0}
                </Typography>
              </Stack>
              <Stack justifyContent={"center"}>
                <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                  Лайков
                </Typography>
                <Typography level="body-sm" sx={{ textAlign: 'center', fontWeight: 600 }}>
                  {union.likes_count || 0}
                </Typography>
              </Stack>
              <Stack justifyContent={"center"}>
                <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                  Статус
                </Typography>
                {isEditing ? (
                    <Select
                        value={editedUnion.status}
                        onChange={(e, value) => setEditedUnion(prev => ({...prev, status: value}))}
                        size="sm"
                    >
                        <Option value="together">Вместе</Option>
                        <Option value="separate">Раздельно</Option>
                    </Select>
                ) : (
                    <Typography level="body-sm" sx={{ textAlign: 'center', fontWeight: 600 }}>
                        {union.status_title || ""}
                    </Typography>
                )}
              </Stack>
            </Stack>
          </Sheet>
        </Stack>
        
        {union.description && (
            isEditing ? (
                <Input
                  value={editedUnion.description}
                  onChange={(e) => setEditedUnion(prev => ({...prev, description: e.target.value}))}
                  placeholder="Описание союза"
                />
            ) : union.description ? (
                <Typography level="body-sm">
                  {union.description}
                </Typography>
            ) : null
        )}

        {/* Добавляем кнопку завершения союза в секцию редактирования */}
        {isEditing && isAdmin && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              color="danger"
              onClick={handleEndUnion}
              loading={isEndingUnion}
              startDecorator={<DeleteRoundedIcon />}
            >
              Завершить союз
            </Button>
          </Box>
        )}


        {/* Участники */}
        {/* <Stack spacing={1}>
          <Typography level="h4">Участники</Typography>
          <Stack direction="row" spacing={2}>
            {union.members?.map((member) => (
              <Stack key={member.id} alignItems="center" spacing={1}>
                <Avatar 
                  src={member.profile?.photo_file_path} 
                  size="lg" 
                />
                <Typography level="body-sm" textAlign="center">
                  {member.profile?.name}
                </Typography>
                <Chip size="sm" color={member.role === 'admin' ? 'primary' : 'neutral'}>
                  {member.role === 'admin' ? 'Админ' : 'Участник'}
                </Chip>
              </Stack>
            ))}
          </Stack>
        </Stack> */}

        {/* Лента постов */}
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h4">Посты</Typography>
                    
                    {/* Кнопка добавления поста для участников */}
                    {isAdmin && !showCreatePost && (
                        <Button
                            onClick={() => setShowCreatePost(true)}
                            startDecorator={<AddRoundedIcon />}
                        >
                            Добавить пост
                        </Button>
                    )}
            </Stack>
                    
            {/* Форма создания поста */}
            {showCreatePost && (
                <CreatePostForm
                    onSubmit={handleCreatePost}
                    onCancel={() => setShowCreatePost(false)}
                />
            )}

          {posts.length === 0 ? (
            <Typography level="body-lg" textAlign="center" sx={{ py: 4 }}>
              Пока нет постов
            </Typography>
          ) : (
            posts.map((post) => (
              <UnionPost
                key={post.id}
                post={post}
                onLike={onLikePost}
                onCreateComment={onCreateComment}
                onDelete={onDeletePost}
                token={token}
              />
            ))
          )}
        </Stack>
      </Stack>
    </Box>
  );
}